// Bulk download server side downloader modal component.
// This is the 'downloading' stage of BulkDownloadModal.
// Initiates bulk download and reports progress to the user.
// We formerly had a client-side bulk downloader as well, which has been deleted.

import React, { useEffect, useRef, useState } from 'react';
import styled from 'astroturf/react';
import { captureException } from '@sentry/browser';

import {
  getBulkDownloadServerSideZipUrl,
  BulkDownloadFilesResponse,
  BulkDownloadFile,
} from 'js/bulk_download/bulk_download_helper';
import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';
import { Banner } from 'js/design_system/Banner';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Modal } from 'js/design_system/Modal';

const STAGES = {
  // In progress
  fetchingFileList: 'fetchingFileList',
  zipping: 'zipping',

  // End states
  success: 'success',
  error: 'error',
};

// The progress bar is fake because the Lambda function does not actually send progress to the client
// For now, progress updates linearly until the Lambda is done and provides a download URL
// If lambda is not finished by the time the bar reaches 95%, it stays there until Lambda is done
function useFakeProgress(filesTotal: number, isActive: boolean): number {
  const progressRef = useRef(0);
  const [, forceUpdate] = React.useState(0);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const increment = 95 / ((filesTotal + 1) * 4);
    const id = window.setInterval(() => {
      const next = progressRef.current + increment;
      if (next < 95) {
        progressRef.current = next;
        forceUpdate((n) => n + 1);
      }
    }, 334);

    return () => {
      clearInterval(id);
    };
  }, [filesTotal, isActive]);

  return isActive ? progressRef.current : 100;
}

type Stage = (typeof STAGES)[keyof typeof STAGES];

type BulkDownloadFlowResult = {
  stage: Stage;
  bytesTotal: number | null;
  zipUrl: string | null;
  timings: {
    fetchStart: number;
    zipStart: number | null;
    zipComplete: number | null;
  };
};

// Orchestrates the download lifecycle: fetchFileList -> zip -> success/error
function useBulkDownloadFlow(
  loadBulkDownloadFiles: () => Promise<BulkDownloadFilesResponse>,
  bundleDescription: string,
  onZipUrlSuccess?: (files: BulkDownloadFile[]) => void,
): BulkDownloadFlowResult {
  // Current stage of the download flow
  const [stage, setStage] = useState<Stage>(STAGES.fetchingFileList);
  const [bytesTotal, setBytesTotal] = useState<number | null>(null);
  // The final zip file AWS S3 url
  const [zipUrl, setZipUrl] = useState<string | null>(null);
  const timingsRef = useRef({
    fetchStart: Math.floor(window.performance.now()),
    zipStart: null as number | null,
    zipComplete: null as number | null,
  });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    loadBulkDownloadFiles().then(
      (fileList) => {
        if (cancelledRef.current) return;

        const totalBytes = fileList.files
          .map((file) => file.filesize)
          .reduce((a, b) => a + b, 0);
        setBytesTotal(totalBytes);
        timingsRef.current.zipStart = Math.floor(window.performance.now());
        setStage(STAGES.zipping);

        getBulkDownloadServerSideZipUrl(
          fileList.files,
          bundleDescription,
          fileList.zipperUrl,
        ).then(
          (url: string) => {
            if (cancelledRef.current) return;
            timingsRef.current.zipComplete = Math.floor(
              window.performance.now(),
            );
            onZipUrlSuccess?.(fileList.files);
            setZipUrl(url);
            setStage(STAGES.success);
          },
          (err: unknown) => {
            if (cancelledRef.current) return;
            setStage(STAGES.error);
            captureException(err);
          },
        );
      },
      (err: unknown) => {
        if (cancelledRef.current) return;
        setStage(STAGES.error);
        captureException(err);
      },
    );

    return () => {
      cancelledRef.current = true;
    };
    // Run once on mount — props are stable for the lifetime of this component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { stage, bytesTotal, zipUrl, timings: timingsRef.current };
}

type AnalyticsProps = {
  filesTotal: number;
  source: BulkDownloadSource;
  extraTrackingData?: object;
};

// Records analytics once per bulk download attempt: on success, error,
// modal close, or window unload — whichever comes first.
function useBulkDownloadAnalytics(
  flow: BulkDownloadFlowResult,
  props: AnalyticsProps,
): void {
  const sentRef = useRef(false);

  // Keep a ref of the latest data so the beforeunload handler never
  // reads stale closure values
  const dataRef = useRef({ flow, props });
  dataRef.current = { flow, props };

  const recordAnalytics = useRef((whenSent: string) => {
    if (sentRef.current) return;
    sentRef.current = true;

    const { flow: f, props: p } = dataRef.current;
    const data = {
      stage_reached: f.stage,
      when_sent: whenSent,
      source: p.source,
      bytes_total: f.bytesTotal,
      files_total: p.filesTotal,
      fetching_file_list_start: f.timings.fetchStart,
      zipping_start: f.timings.zipStart,
      zip_creation_complete: f.timings.zipComplete,
      ...p.extraTrackingData,
    };

    arbitraryEvents.write('BulkDownloadServerSide', data);
  }).current;

  // Record analytics even if the user closes the window or the modal
  useEffect(() => {
    const onUnload = () => recordAnalytics('on window unload');
    window.addEventListener('beforeunload', onUnload);

    return () => {
      window.removeEventListener('beforeunload', onUnload);
      recordAnalytics('on modal close');
    };
  }, [recordAnalytics]);

  // Record on success or error as soon as the stage changes
  useEffect(() => {
    if (flow.stage === STAGES.success) {
      recordAnalytics('on success');
    } else if (flow.stage === STAGES.error) {
      recordAnalytics('on error');
    }
  }, [flow.stage, recordAnalytics]);
}

// Automatically start the download and close the modal 10s after zip is ready
function useAutoDownload(
  zipUrl: string | null,
  linkRef: React.RefObject<HTMLAnchorElement | null>,
  closeModal: () => void,
): void {
  useEffect(() => {
    if (!zipUrl || !linkRef.current) return;

    linkRef.current.click();
    const timeout = window.setTimeout(closeModal, 10000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipUrl]);
}

type BulkDownloadSource = 'seed-work' | 'books-search-results';

type Props = {
  // The number of files that will be downloaded
  filesTotal: number;
  // Function that closes the current modal
  closeModal: () => void;

  bundleDescription: string;
  loadBulkDownloadFiles: () => Promise<BulkDownloadFilesResponse>;
  onZipUrlSuccess?: (files: BulkDownloadFile[]) => void;
  source: BulkDownloadSource;
  extraTrackingData?: object;
};

export default function ServerSideBulkDownloader({
  filesTotal,
  closeModal,
  bundleDescription,
  loadBulkDownloadFiles,
  onZipUrlSuccess,
  source,
  extraTrackingData,
}: Props) {
  const linkRef = useRef<HTMLAnchorElement | null>(null);

  const flow = useBulkDownloadFlow(
    loadBulkDownloadFiles,
    bundleDescription,
    onZipUrlSuccess,
  );
  const { stage, zipUrl } = flow;

  const isInProgress =
    stage === STAGES.fetchingFileList || stage === STAGES.zipping;
  const progress = useFakeProgress(filesTotal, isInProgress);

  useBulkDownloadAnalytics(flow, { filesTotal, source, extraTrackingData });
  useAutoDownload(zipUrl, linkRef, closeModal);

  const isError = stage === STAGES.error;

  return (
    <CompactModal
      open
      onClose={closeModal}
      showHeader={false}
      containerStyle={{ maxWidth: 'min(95vw, 800px)' }}
    >
      <Container>
        <Heading size="sm">
          {!zipUrl ? 'Preparing your download' : 'Your download is ready'}
        </Heading>

        {!zipUrl && (
          <Subtitle>
            Please don't close this window. It will close automatically when
            your download begins.
          </Subtitle>
        )}

        <ProgressContainer>
          {isError ? (
            <Banner type="danger" icon={<Icon name="error" />}>
              Sorry, something went wrong and we weren't able to complete your
              download. Please make sure that your connection is stable and try
              your download again. If this issue continues, please contact
              support.
            </Banner>
          ) : (
            <Body size="sm" bold>
              {stage === STAGES.fetchingFileList &&
                'Fetching list of papers to download'}
              {stage === STAGES.zipping && 'Bundling your download'}
              {stage === STAGES.success && (
                <>
                  Bundling complete! Your download should start automatically,
                  but if it doesn't, click this{' '}
                  <BodyLink
                    size="sm"
                    ref={linkRef}
                    href={zipUrl || undefined}
                    download={`Academia.edu Bundle - ${bundleDescription}.zip`}
                    underline
                  >
                    link
                  </BodyLink>
                  .
                  <br />
                  This window will close on its own, or you can close it now.
                </>
              )}
            </Body>
          )}
        </ProgressContainer>

        <ProgressBarTrack>
          <ProgressBarFill style={{ width: `${progress}%` }} />
        </ProgressBarTrack>
      </Container>
    </CompactModal>
  );
}

// On mobile the DS Modal defaults to full-screen. This override keeps
// the bulk-download modal compact and centered instead.
const CompactModal = styled(Modal)`
  @media (max-width: 567px) {
    min-height: unset;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const Subtitle = styled(Body)`
  font-style: italic;
`;

const ProgressContainer = styled.div`
  margin-top: 32px;
`;

const ProgressBarTrack = styled.div`
  width: var(--columns-col-4, 344px);
  max-width: 100%;
  height: 16px;
  background: var(--blue-100, #e8eefb);
  border-radius: var(--corner-radius-radius-sm, 4px);
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: var(--primary, #0645b1);
  border-radius: var(--corner-radius-radius-sm, 4px);
  transition: width 0.3s ease-in-out;
`;
